import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import "../styles/job-management.css";
import { getJobsPaginated, deleteJob } from "../services/jobService";
import { useAppContext } from "../context/AppContext";
import { toast } from "../utils/toast";
import { getActionDropdownDirection } from "../utils/dropdownPosition";

const JobManagementPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const lastLoadKeyRef = useRef("");
  const { setActiveSection, setJobId } = useAppContext();

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownDirection, setDropdownDirection] = useState("down");

  const [searchInput, setSearchInput] = useState(
    () => searchParams.get("search") || "",
  );
  const [debouncedSearch, setDebouncedSearch] = useState(() =>
    (searchParams.get("search") || "").trim(),
  );
  const [page, setPage] = useState(() => {
    const p = Number(searchParams.get("page") || 1);
    return Number.isInteger(p) && p > 0 ? p : 1;
  });
  const [limit, setLimit] = useState(() => {
    const l = Number(searchParams.get("limit") || 10);
    return Number.isInteger(l) && l > 0 ? l : 10;
  });
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  });

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getJobsPaginated({
        page,
        limit,
        search: debouncedSearch,
      });
      setList(Array.isArray(result?.data) ? result.data : []);
      setPagination(
        result?.meta || {
          total: 0,
          page: Number(page) || 1,
          limit: Number(limit) || 10,
          totalPages: 1,
        },
      );
      setSelectedIds([]);
    } catch (e) {
      toast.error(e?.message || "Failed to load jobs");
    } finally {
      setLoading(false);
    }
  }, [page, limit, debouncedSearch]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch((searchInput || "").trim());
      setPage(1);
    }, 450);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const pageStr = String(page);
    const limitStr = String(limit);
    const searchStr = String(searchInput || "").trim();

    if (params.get("page") !== pageStr) params.set("page", pageStr);
    if (params.get("limit") !== limitStr) params.set("limit", limitStr);
    if (searchStr) params.set("search", searchStr);
    else params.delete("search");

    const current = searchParams.toString();
    const next = params.toString();
    if (current !== next) {
      setSearchParams(params, { replace: true });
    }
  }, [page, limit, searchInput, searchParams, setSearchParams]);

  useEffect(() => {
    const key = `${page}:${limit}:${debouncedSearch}`;
    if (lastLoadKeyRef.current === key) return;
    lastLoadKeyRef.current = key;
    reload();
  }, [page, limit, debouncedSearch, reload]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest(".actions-menu-wrapper")) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Close dropdown on Escape
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") setActiveDropdown(null);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const filteredList = useMemo(() => list || [], [list]);

  const handleView = (id) => {
    try {
      localStorage.setItem("activeJobId", String(id));
    } catch {}
    setJobId(id);
    setActiveSection("job-detail", { jobId: id });
  };

  const handleEdit = (id) => {
    try {
      localStorage.setItem("activeJobId", String(id));
    } catch {}
    setJobId(id);
    setActiveSection("job-edit", { jobId: id });
  };

  const handleDeleteJob = async (id, name) => {
    if (!id) return;
    try {
      setList((prev) => prev.filter((j) => String(j.job_id) !== String(id)));
      setSelectedIds((prev) => prev.filter((selectedId) => selectedId !== id));
      await deleteJob(id);
      toast.success(`Job "${name || "this job"}" has been deleted successfully!`);
      await reload();
    } catch (e) {
      toast.error(e?.message || "Failed to delete job");
      await reload();
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    const idsToDelete = [...selectedIds];
    try {
      setBulkDeleting(true);
      for (const id of idsToDelete) {
        await deleteJob(id);
      }
      toast.success(
        `${idsToDelete.length} job${idsToDelete.length > 1 ? "s" : ""} deleted successfully!`,
      );
      setSelectedIds([]);
      await reload();
    } catch (e) {
      toast.error(e?.message || "Failed to delete some jobs");
      await reload();
    } finally {
      setBulkDeleting(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredList.length && filteredList.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredList.map((j) => j.job_id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ?
        prev.filter((selectedId) => selectedId !== id)
      : [...prev, id],
    );
  };

  const clearSelection = () => {
    setSelectedIds([]);
  };

  const totalPages = Math.max(1, Number(pagination.totalPages) || 1);
  const currentPage = Math.min(
    Math.max(1, Number(pagination.page) || page),
    totalPages,
  );
  const totalRows = Number(pagination.total) || 0;
  const pageSize = Number(pagination.limit) || limit;
  const showingFrom = totalRows === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const showingTo =
    totalRows === 0 ? 0 : Math.min(currentPage * pageSize, totalRows);

  const visiblePageItems = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (currentPage <= 2) {
      return [1, 2, 3, "ellipsis", totalPages];
    }

    if (currentPage === 3) {
      return [1, 2, 3, 4, "ellipsis", totalPages];
    }

    if (currentPage === totalPages - 2) {
      return [
        1,
        "ellipsis",
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }

    if (currentPage >= totalPages - 1) {
      return [1, "ellipsis", totalPages - 2, totalPages - 1, totalPages];
    }

    return [
      1,
      "ellipsis",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "ellipsis",
      totalPages,
    ];
  }, [totalPages, currentPage]);

  const limitOptions = useMemo(() => {
    const base = [5, 10, 20, 50];
    const current = Number(limit);
    const all =
      Number.isInteger(current) && current > 0 ? [...base, current] : base;
    return Array.from(new Set(all)).sort((a, b) => a - b);
  }, [limit]);

  return (
    <div className="page-container job-page">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">Job Management</h2>
            <p className="page-subtitle">
              Browse jobs. Use actions to view or edit.
            </p>
          </div>
          <div className="roles-toolbar">
            <div className="roles-search">
              <input
                className="search-input"
                type="text"
                placeholder="Search by name, code, company, function"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <button
                className="secondary-btn sm"
                onClick={() => setSearchInput("")}
                disabled={loading}
              >
                Clear
              </button>
            </div>
            <button
              className="primary-btn"
              onClick={() => setActiveSection("job-create")}
            >
              + Create Job
            </button>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="job-pagination-top">
          <label htmlFor="job-limit" className="job-pagination-label">
            Rows per page:
          </label>
          <select
            id="job-limit"
            value={limit}
            onChange={(e) => {
              const next = Number(e.target.value) || 10;
              setLimit(next);
              setPage(1);
            }}
          >
            {limitOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>

        {selectedIds.length > 0 && (
          <div
            style={{
              marginBottom: "1rem",
              padding: "1rem 1.25rem",
              background: "var(--accent-tint-50)",
              borderRadius: "0.75rem",
              border: "1px solid var(--accent-tint-200)",
              boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "0.75rem",
              }}
            >
              <span style={{ fontWeight: "600", color: "var(--accent-ink-800)", fontSize: "0.9375rem" }}>
                {selectedIds.length} job{selectedIds.length > 1 ? "s" : ""} selected
              </span>
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                <button
                  className="danger-btn sm"
                  onClick={handleBulkDelete}
                  disabled={bulkDeleting}
                >
                  {bulkDeleting ? "Deleting..." : "Delete Selected"}
                </button>
                <button className="secondary-btn sm" onClick={clearSelection}>
                  Clear Selection
                </button>
              </div>
            </div>
          </div>
        )}

        {loading ?
          <div className="no-results">Loading jobs...</div>
        : <div className="roles-table">
            <div className="roles-table-header">
              <div className="cell checkbox">
                <input
                  type="checkbox"
                  checked={
                    selectedIds.length > 0 &&
                    selectedIds.length === filteredList.length &&
                    filteredList.length > 0
                  }
                  onChange={toggleSelectAll}
                />
              </div>
              <div className="cell">Job Code</div>
              <div className="cell">Job Name</div>
              <div className="cell">Function</div>
              <div className="cell">Company</div>
              <div className="cell">Category</div>
              <div className="cell">Status</div>
              <div className="cell">Version</div>
              <div className="cell actions">Actions</div>
            </div>

            {!filteredList || filteredList.length === 0 ?
              <div className="no-results">No jobs found</div>
            : filteredList.map((j) => (
                <div
                  key={j.job_id}
                  className={`roles-table-row ${selectedIds.includes(j.job_id) ? "selected" : ""}`}
                >
                  <div className="cell checkbox">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(j.job_id)}
                      onChange={() => toggleSelect(j.job_id)}
                    />
                  </div>
                  <div className="cell">{j.jobCode || "-"}</div>
                  <div className="cell">{j.name || "-"}</div>
                  <div className="cell">
                    {j.function?.name ||
                      j.Function?.name ||
                      j.function_name ||
                      (j.function_id != null ? `#${j.function_id}` : "-")}
                  </div>
                  <div className="cell">
                    {j.company?.name || j.company_name || "-"}
                  </div>
                  <div className="cell">
                    {j.category?.name ||
                      j.category?.description ||
                      j.jobCategory?.name ||
                      j.jobCategory?.description ||
                      j.category_name ||
                      "-"}
                  </div>
                  <div className="cell">
                    {j.status?.description ||
                      j.status?.name ||
                      j.jobStatus?.description ||
                      j.jobStatus?.name ||
                      j.status_name ||
                      "-"}
                  </div>
                  <div className="cell">
                    {j.job_version || j.version || "-"}
                  </div>
                  <div className="cell actions">
                    <div
                      className="actions-menu-wrapper"
                      style={{ position: "relative", display: "inline-block" }}
                    >
                      <button
                        className="action-menu-btn"
                        aria-haspopup="true"
                        aria-expanded={activeDropdown === j.job_id}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (activeDropdown === j.job_id) {
                            setActiveDropdown(null);
                          } else {
                            setDropdownDirection(getActionDropdownDirection(e.currentTarget));
                            setActiveDropdown(j.job_id);
                          }
                        }}
                      >
                        Actions ▼
                      </button>
                      {activeDropdown === j.job_id && (
                        <div
                          className={`action-dropdown-menu${
                            dropdownDirection === "up" ? " dropdown-up" : ""
                          }`}
                          role="menu"
                        >
                          <button
                            className="action-menu-item"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                              handleView(j.job_id);
                            }}
                          >
                            View
                          </button>
                          <button
                            className="action-menu-item"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                              handleEdit(j.job_id);
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="action-menu-item danger"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                                handleDeleteJob(j.job_id, j.name);
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            }
          </div>
        }

        <div className="job-pagination-bottom">
          <div className="job-pagination-summary">
            Showing {showingFrom} to {showingTo} of {totalRows} entries
          </div>
          <div className="job-pagination-actions">
            <button
              className="secondary-btn sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1 || loading}
            >
              Previous
            </button>
            {visiblePageItems.map((item, index) => {
              if (item === "ellipsis") {
                return (
                  <span
                    key={`ellipsis-${index}`}
                    style={{ padding: "0 6px", color: "var(--gray-500)" }}
                    aria-hidden="true"
                  >
                    …
                  </span>
                );
              }
              const p = Number(item);
              return (
                <button
                  key={p}
                  className={
                    p === currentPage ? "primary-btn sm" : "secondary-btn sm"
                  }
                  onClick={() => setPage(p)}
                  disabled={loading}
                >
                  {p}
                </button>
              );
            })}
            <button
              className="secondary-btn sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages || loading}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobManagementPage;

